import { createTheme, Theme } from "@mui/material/styles";
import * as colorsXnuup from "./colors";
import { DefaultTheme } from "styled-components";

export const createMuiTheme = (colors: DefaultTheme) => {
	// Notation: this font size is calculated based on the old font size
	// before Upgrade to MUI v5, which was 13px.
	const OLD_FONT_SIZE = "0.9285714285714286rem";
	return createTheme({
		headerColor: colors.headerColor,
		palette: {
			common: {
				black: colors.pantoneBlack,
				white: colors.pantone656C,
			},
			background: { default: "#fafafa" },
			primary: {
				main: colors.pantone2726C,
				light: colors.pantone656C,
			},
			secondary: {
				main: colors.pantone656C,
				contrastText: colors.pantone2726C,
			},

			error: {
				light: "#e57373",
				main: "#f44336",
				dark: "#d32f2f",
				contrastText: "#fff",
			},
			text: {
				primary: colors.pantoneBlack,
				secondary: "rgba(153, 153, 153, 1)",
				disabled: "rgba(0, 0, 0, 0.38)",
			},
			action: {
				focus: colors.pantone2726C,
				disabled: colors.pantone2726C,
			},
		},
		typography: {
			fontSize: 13,
			htmlFontSize: 16,
			fontFamily: "Wix Madefor Text, Roboto, Helvetica, Arial, sans-serif",

			body1: {
				fontSize: "0.8125rem",
			},
		},
		components: {
			MuiAccordion: {
				styleOverrides: {
					gutters: {
						color: colors.pantoneBlack,
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					option: {
						fontSize: OLD_FONT_SIZE,
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					caption: {
						fontSize: "0.875rem",
					},
					overline: {
						fontSize: "0.875rem",
					},
					button: {
						fontSize: "0.15rem",
					},
					body1: {
						fontSize: "0.875rem",
					},
					h2: {
						fontSize: "0.85rem",
						fontWeight: 600,
						letterSpacing: "0.02em",
					},
					subtitle1: {
						fontSize: "0.7rem",
						fontWeight: 600,
					},
					subtitle2: {
						fontSize: "0.67rem",
						fontWeight: 400,
						lineHeight: 1.8,
					},
					h1: {
						fontWeight: 500,
						fontSize: "2rem",
						lineHeight: 1.5,
					},
					h3: {
						fontSize: "2.5rem",
					},
					root: {
						fontFamily: "Wix Madefor Text, Roboto, Helvetica, Arial, sans-serif",
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					containedPrimary: {
						backgroundColor: "#00A3B3",
						"&:hover": {
							backgroundColor: "#00A3B3",
						},
					},
					containedSecondary: {
						backgroundColor: "#E85450",
						"&:hover": {
							backgroundColor: "#E85450",
						},
					},
					root: { minWidth: 100 },
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						fontSize: OLD_FONT_SIZE,
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						whiteSpace: "nowrap",
						fontSize: OLD_FONT_SIZE,
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: { padding: 0, color: colors.pantone2726C },
					colorPrimary: { color: colors.pantone2726C },
				},
			},
			MuiInput: {
				styleOverrides: {
					input: {
						color: "rgba(153, 153, 153, 1)",
						fontWeight: 500,
						fontSize: 12,
						letterSpacing: 1,
					},
					underline: {
						"&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused):before": {
							borderBottom: `2px solid ${colors.pantone2726C}`,
						},
						"&:after": {
							borderBottom: `2px solid ${colors.pantone2726C}`,
						},
					},
				},
			},
			MuiFilledInput: {
				styleOverrides: {
					underline: {
						"&:hover:not(.Mui-disabled):not(.Mui-error):before": {
							borderBottom: "none",
						},
					},
				},
			},
			MuiInputBase: {
				styleOverrides: {
					input: { padding: "6px 0 4px", fontSize: OLD_FONT_SIZE },
				},
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"&:focus": {
							backgroundColor: "#fff",
						},
						"&.Mui-selected": {
							backgroundColor: "#fff",
						},
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					content: {
						margin: 4,
						"&.Mui-expanded": { margin: 4 },
					},
					root: {
						minHeight: 32,
						"&.Mui-expanded": { minHeight: 32 },
						background: "rgba(153, 153, 153, 1)",
						"& span": {
							color: colors.pantoneBlack,
							fontWeight: "bold",
						},
						"& p": {
							letterSpacing: "0.01em",
							fontWeight: 525,
							color: "#fff",
						},
					},
				},
			},
		},
	});
};

export const getCommonColors = (theme: any) => {
	return theme.palette.common;
};

export const getSecondaryColors = (theme: any) => {
	return theme.palette.secondary;
};

export const getActionColors = (theme: Theme) => {
	return theme.palette.action;
};

const defaultTheme = createMuiTheme(colorsXnuup.default as any);
export default defaultTheme;
